import React, { useEffect, useMemo, useRef, useState } from "react";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { Form } from "react-bootstrap";
import { useGooglePlacesLoader } from "../hooks/useGooglePlacesLoader";
import { useFormContext } from "../hooks/useMultiStepForm";
import {
  formatPhoneValue,
  getAddressProfileDefaults,
  getCountryCodeFromName,
  getSuggestionLabel,
  normalizePlaceAddress,
} from "./addressProfileUtils";

const ADDRESS_AUTOCOMPLETE_PRIMARY_TYPES = ["street_address", "premise", "subpremise"];

function SearchIcon({ className = "" }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7"></circle>
      <path d="m20 20-3.5-3.5"></path>
    </svg>
  );
}

function getPlacesLibrary() {
  if (!window.google?.maps) {
    return null;
  }

  return window.google.maps.places || null;
}

export function AddressProfileFields({ initialDetails }) {
  const formContext = useFormContext();
  const savedFormDetails = formContext?.formDetails || null;
  const defaultFormValues = useMemo(() => {
    return getAddressProfileDefaults({
      ...initialDetails,
      ...savedFormDetails,
    });
  }, [initialDetails, savedFormDetails]);
  const [formValues, setFormValues] = useState(() => defaultFormValues);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressLine2Ref = useRef(null);
  const containerRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const skipAutocompleteRef = useRef(false);
  const activeRequestRef = useRef(0);
  const { isLoaded, loadError } = useGooglePlacesLoader();

  useEffect(() => {
    setFormValues(defaultFormValues);
  }, [defaultFormValues]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const selectedCountryCode = useMemo(() => {
    return getCountryCodeFromName(formValues.country);
  }, [formValues.country]);

  useEffect(() => {
    let ignore = false;
    const currentRequestId = activeRequestRef.current + 1;
    activeRequestRef.current = currentRequestId;

    async function fetchSuggestions() {
      if (skipAutocompleteRef.current) {
        skipAutocompleteRef.current = false;
        return;
      }

      if (!isLoaded || loadError) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      if (!formValues.address.trim() || formValues.address.trim().length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const placesLibrary = getPlacesLibrary();
      if (!placesLibrary?.AutocompleteSuggestion || !placesLibrary?.AutocompleteSessionToken) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        sessionTokenRef.current = sessionTokenRef.current || new placesLibrary.AutocompleteSessionToken();

        const response = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: formValues.address,
          includedPrimaryTypes: ADDRESS_AUTOCOMPLETE_PRIMARY_TYPES,
          includedRegionCodes: selectedCountryCode ? [selectedCountryCode.toLowerCase()] : undefined,
          sessionToken: sessionTokenRef.current,
        });

        if (ignore || activeRequestRef.current !== currentRequestId) {
          return;
        }

        const nextSuggestions = response?.suggestions || [];
        setSuggestions(nextSuggestions);
        setShowSuggestions(Boolean(nextSuggestions.length));
      } catch (error) {
        if (!ignore) {
          console.error("Google Places autocomplete failed", error);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    }

    const debounceId = window.setTimeout(fetchSuggestions, 250);

    return () => {
      ignore = true;
      window.clearTimeout(debounceId);
    };
  }, [formValues.address, isLoaded, loadError, selectedCountryCode]);

  function updateFormValue(field, value) {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function handleSuggestionSelect(suggestion) {
    try {
      const prediction = suggestion?.placePrediction;
      if (!prediction) {
        return;
      }

      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const normalizedAddress = normalizePlaceAddress(place);

      skipAutocompleteRef.current = true;
      sessionTokenRef.current = null;
      setSuggestions([]);
      setShowSuggestions(false);
      setFormValues((prev) => ({
        ...prev,
        ...normalizedAddress,
        addressLine2: prev.addressLine2,
      }));

      if (addressLine2Ref.current) {
        addressLine2Ref.current.focus();
      }
    } catch (error) {
      console.error("Google Places place details failed", error);
    }
  }

  return (
    <div className="space-y-3" ref={containerRef}>
      <Form.Group>
        <CountryDropdown
          id="country"
          name="country"
          aria-label="Country / Region"
          country={formValues.country}
          value={formValues.country}
          onChange={(nextCountry) => {
            skipAutocompleteRef.current = true;
            sessionTokenRef.current = null;
            setSuggestions([]);
            setShowSuggestions(false);
            setFormValues((prev) => ({
              ...prev,
              country: nextCountry,
              state: "",
            }));
          }}
          classes="form-select"
          showDefaultOption={false}
          required
        />
      </Form.Group>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Form.Group>
          <Form.Control
            id="firstName"
            name="firstName"
            type="text"
            aria-label="First Name"
            required
            value={formValues.firstName}
            onChange={(event) => updateFormValue("firstName", event.target.value)}
            placeholder="First name"
          />
        </Form.Group>

        <Form.Group>
          <Form.Control
            id="lastName"
            name="lastName"
            type="text"
            aria-label="Last Name"
            required
            value={formValues.lastName}
            onChange={(event) => updateFormValue("lastName", event.target.value)}
            placeholder="Last name"
          />
        </Form.Group>
      </div>

      <Form.Group>
        <div className="relative">
          <Form.Control
            id="address"
            name="address"
            type="text"
            aria-label="Address"
            required
            autoComplete="shipping address-line1"
            value={formValues.address}
            onChange={(event) => updateFormValue("address", event.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setShowSuggestions(false);
              }
            }}
            placeholder="Address"
            className="pe-5"
          />
          <SearchIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl">
              <div className="max-h-72 overflow-y-auto py-2">
                {suggestions.map((suggestion, index) => {
                  const label = getSuggestionLabel(suggestion);

                  return (
                    <button
                      key={`${label}-${index}`}
                      type="button"
                      className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={() => handleSuggestionSelect(suggestion)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-slate-100 px-4 py-2 text-right text-xs font-medium text-slate-400">
                Powered by Google Maps
              </div>
            </div>
          )}
        </div>
      </Form.Group>

      <Form.Group>
        <Form.Control
          id="addressLine2"
          name="addressLine2"
          type="text"
          aria-label="Apartment, unit, or PO box"
          ref={addressLine2Ref}
          autoComplete="shipping address-line2"
          value={formValues.addressLine2}
          onChange={(event) => updateFormValue("addressLine2", event.target.value)}
          placeholder="Apartment, Unit, P.O. box, etc. (optional)"
        />
      </Form.Group>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Form.Group>
          <Form.Control
            id="city"
            name="city"
            type="text"
            aria-label="City"
            required
            autoComplete="shipping address-level2"
            value={formValues.city}
            onChange={(event) => updateFormValue("city", event.target.value)}
            placeholder="City"
          />
        </Form.Group>

        <Form.Group>
          <RegionDropdown
            id="state"
            name="state"
            aria-label="Province / Territory / State"
            country={formValues.country}
            value={formValues.state}
            onChange={(nextState) => updateFormValue("state", nextState)}
            classes="form-select"
            defaultOptionLabel="Select province/state"
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Control
            id="postalCode"
            name="postalCode"
            type="text"
            aria-label="Postal Code"
            required
            autoComplete="shipping postal-code"
            value={formValues.postalCode}
            onChange={(event) => updateFormValue("postalCode", event.target.value)}
            placeholder="Postal code"
          />
        </Form.Group>
      </div>

      <Form.Group>
        <Form.Control
          id="phoneNumber"
          name="phoneNumber"
          type="text"
          aria-label="Phone Number"
          required
          maxLength={12}
          minLength={10}
          pattern="\d{3}-\d{3}-\d{4}"
          autoComplete="tel"
          value={formValues.phoneNumber}
          onChange={(event) => updateFormValue("phoneNumber", formatPhoneValue(event.target.value))}
          placeholder="Phone"
        />
      </Form.Group>
    </div>
  );
}

export default AddressProfileFields;
